import React from 'react';
import { Route, Clock, TrendingDown, Eye, EyeOff } from 'lucide-react';

/**
 * RouteSummary — compact card displayed inside TodaysVisits after route optimization.
 *
 * Props:
 *   optimizedRoute     — RouteOptimizeResponse from backend, or null
 *   showRouteOnMap     — boolean, whether the route line is visible on the map
 *   onToggleRouteOnMap — callback to toggle map visibility
 */
const RouteSummary = ({ optimizedRoute, showRouteOnMap, onToggleRouteOnMap }) => {
    if (!optimizedRoute) return null;

    const {
        total_distance_km,
        estimated_total_minutes,
        distance_saved_km,
        time_saved_minutes,
    } = optimizedRoute;

    const hasSavings = distance_saved_km != null && distance_saved_km > 0;

    return (
        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
            {/* Header row */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <Route className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-bold text-blue-800">Optimized Route</span>
                </div>
                <button
                    onClick={onToggleRouteOnMap}
                    className="flex items-center gap-1 text-xs font-semibold text-blue-500 hover:text-blue-700 transition-colors px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded-md"
                    title={showRouteOnMap ? 'Hide route on map' : 'Show route on map'}
                >
                    {showRouteOnMap ? (
                        <>
                            <EyeOff className="w-3 h-3" />
                            Hide Route
                        </>
                    ) : (
                        <>
                            <Eye className="w-3 h-3" />
                            Show Route
                        </>
                    )}
                </button>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-md px-2 py-1.5 border border-blue-100">
                    <div className="flex items-center gap-1 mb-0.5">
                        <Route className="w-3 h-3 text-blue-400" />
                        <span className="text-xs text-blue-600 font-medium">Distance</span>
                    </div>
                    <span className="text-sm font-bold text-blue-800">
                        {total_distance_km != null ? `${total_distance_km.toFixed(1)} km` : '—'}
                    </span>
                </div>

                <div className="bg-white rounded-md px-2 py-1.5 border border-blue-100">
                    <div className="flex items-center gap-1 mb-0.5">
                        <Clock className="w-3 h-3 text-blue-400" />
                        <span className="text-xs text-blue-600 font-medium">Est. Travel</span>
                    </div>
                    <span className="text-sm font-bold text-blue-800">
                        {estimated_total_minutes != null ? `${Math.round(estimated_total_minutes)} min` : '—'}
                    </span>
                </div>
            </div>

            {/* Savings line — only shown when there's a measurable saving */}
            {hasSavings && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-green-700 font-semibold">
                    <TrendingDown className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                        Saving {distance_saved_km.toFixed(1)} km
                        {time_saved_minutes != null && time_saved_minutes > 0
                            ? ` / ${Math.round(time_saved_minutes)} min`
                            : ''}{' '}
                        vs original order
                    </span>
                </div>
            )}
        </div>
    );
};

export default RouteSummary;
